// src/utils/labelPrint.ts
// Shared barcode label rendering + EXACT-size printing for thermal label printers.
// - @page size = roll width × label height, margin 0 → no date/time/URL header-footer
// - 1/2/3/4 labels per row (multi-up rolls)
// - templates: stacked | text-left | barcode-left | barcode-top
// - per-field font size (px), barcode height/width, alignment, bold name, border
// - same CSS for preview & print → WYSIWYG

import JsBarcode from 'jsbarcode';

export type LabelBarcodeFormat = 'EAN13' | 'CODE128';
export type LabelTemplate = 'stacked' | 'text-left' | 'barcode-left' | 'barcode-top';

export interface LabelFieldFlags {
  companyName?: boolean;
  productName?: boolean;
  brand?: boolean;
  category?: boolean;
  mrp?: boolean;
  saleRate?: boolean;
  hsnCode?: boolean;
  articleNo?: boolean;
  barcodeImage?: boolean;
  barcodeNumber?: boolean;
  sizeColor?: boolean;
  customText?: boolean;
}

/** Font size (px) for each text field */
export interface LabelFieldSizes {
  companyName: number;
  productName: number;
  brand: number;
  category: number;
  hsnCode: number;
  articleNo: number;
  sizeColor: number;
  customText: number;
  mrp: number;
  saleRate: number;
  barcodeNumber: number;
}

export const DEFAULT_FIELD_SIZES: LabelFieldSizes = {
  companyName: 9,
  productName: 10,
  brand: 8,
  category: 8,
  hsnCode: 8,
  articleNo: 8,
  sizeColor: 8,
  customText: 8,
  mrp: 9,
  saleRate: 9,
  barcodeNumber: 8,
};

export interface LabelData {
  barcode: string;
  companyName?: string;
  productName?: string;
  brand?: string;
  category?: string;
  hsnCode?: string;
  articleNo?: string;
  sizeColor?: string;
  mrp?: string;
  saleRate?: string;
  customText?: string;
}

export interface LabelLayout {
  template: LabelTemplate;
  columns: number; // labels per row (1-4)
  colGap: number; // mm gap between labels in a row
  sideMargin: number; // mm left/right margin of the roll
  barcodeHeight: number; // 0.3 small | 0.4 medium | 0.5 large
  barcodeHeightMm: number | null; // exact barcode height in mm (null = auto)
  barcodeWidth: number; // side templates: share of label width for barcode (0.35 / 0.45 / 0.55)
  align: 'left' | 'center';
  boldName: boolean;
  border: boolean;
  sizes: LabelFieldSizes;
}

export const DEFAULT_LAYOUT: LabelLayout = {
  template: 'stacked',
  columns: 1,
  colGap: 2,
  sideMargin: 0,
  barcodeHeight: 0.4,
  barcodeWidth: 0.45,
  barcodeHeightMm: null,
  align: 'left',
  boldName: true,
  border: false,
  sizes: DEFAULT_FIELD_SIZES,
};

const TEMPLATES: LabelTemplate[] = ['stacked', 'text-left', 'barcode-left', 'barcode-top'];
const JSBARCODE_CDN = 'https://cdn.jsdelivr.net/npm/jsbarcode@3.11.6/dist/JsBarcode.all.min.js';

const clamp = (v: number, min: number, max: number) => Math.min(Math.max(v, min), max);
const num = (v: unknown, d: number) => (Number.isFinite(Number(v)) && v !== '' && v !== null ? Number(v) : d);

export function clampLabelSize(w: number, h: number): { w: number; h: number } {
  return { w: clamp(Number(w) || 50, 15, 200), h: clamp(Number(h) || 25, 10, 200) };
}

export function normalizeSizes(s?: Partial<LabelFieldSizes> | null): LabelFieldSizes {
  const m = { ...DEFAULT_FIELD_SIZES, ...(s || {}) };
  const out = {} as LabelFieldSizes;
  (Object.keys(DEFAULT_FIELD_SIZES) as (keyof LabelFieldSizes)[]).forEach((k) => {
    out[k] = clamp(num(m[k], DEFAULT_FIELD_SIZES[k]), 4, 60);
  });
  return out;
}

export function normalizeLayout(l?: Partial<LabelLayout> | null): LabelLayout {
  const m = { ...DEFAULT_LAYOUT, ...(l || {}) };
  return {
    template: 'stacked', // templates disabled for now
    columns: clamp(Math.round(num(m.columns, 1)), 1, 4),
    colGap: clamp(num(m.colGap, 2), 0, 20),
    sideMargin: clamp(num(m.sideMargin, 0), 0, 20),
    barcodeHeight: clamp(num(m.barcodeHeight, 0.4), 0.2, 0.7),
    barcodeWidth: clamp(num(m.barcodeWidth, 0.45), 0.25, 0.75),
    barcodeHeightMm:
      m.barcodeHeightMm == null || !Number.isFinite(Number(m.barcodeHeightMm)) || Number(m.barcodeHeightMm) <= 0
        ? null
        : clamp(Number(m.barcodeHeightMm), 2, 200),
    align: m.align === 'center' ? 'center' : 'left',
    boldName: m.boldName !== false,
    border: !!m.border,
    sizes: normalizeSizes(m.sizes),
  };
}

/** Effective barcode height in mm (manual mm if set, else auto from label height) */
export function barcodeHeightMm(w: number, h: number, layout?: Partial<LabelLayout> | null): number {
  const L = normalizeLayout(layout);
  const pad = clamp(Math.min(w, h) * 0.05, 1, 3);
  const maxH = Math.max(h - pad * 2, 2);
  if (L.barcodeHeightMm != null) return +Math.min(L.barcodeHeightMm, maxH).toFixed(2);
  const isSide = L.template === 'text-left' || L.template === 'barcode-left';
  const auto = isSide ? (h - pad * 2) * Math.min(L.barcodeHeight * 1.5, 0.85) : h * L.barcodeHeight;
  return +Math.min(auto, maxH).toFixed(2);
}

/** Total roll width (mm) = side margins + labels + gaps */
export function rollWidth(w: number, layout?: Partial<LabelLayout> | null): number {
  const L = normalizeLayout(layout);
  return +(L.sideMargin * 2 + L.columns * w + (L.columns - 1) * L.colGap).toFixed(2);
}

export function toBarcodeFormat(f: string | undefined): LabelBarcodeFormat {
  return f === 'EAN13' ? 'EAN13' : 'CODE128';
}

function esc(v: unknown): string {
  return String(v ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

const has = (v: unknown) => v !== undefined && v !== null && String(v).trim() !== '';
const str = (v: unknown): string | undefined =>
  v === undefined || v === null || String(v).trim() === '' ? undefined : String(v);

/** Map any cart/print item shape to LabelData (supports name/itemName, hsnCode/hsn_code etc.) */
export function toLabelData(item: object, companyName?: string, customText?: string): LabelData {
  const it = item as Record<string, unknown>;
  const sizeColor = [str(it.size), str(it.color)].filter(Boolean).join(' / ');
  return {
    barcode: str(it.barcode) ?? '',
    companyName,
    productName: str(it.name ?? it.itemName),
    brand: str(it.brand),
    category: str(it.category),
    hsnCode: str(it.hsnCode ?? it.hsn_code),
    articleNo: str(it.articleNo ?? it.article_no),
    sizeColor: sizeColor || undefined,
    mrp: str(it.mrp),
    saleRate: str(it.saleRate ?? it.sale_rate),
    customText,
  };
}

/** Barcode SVG that stretches to fill its box (box size is controlled by CSS in mm) */
export function makeBarcodeSvg(value: string, format: LabelBarcodeFormat): string {
  if (typeof JsBarcode === 'function') {
    try {
      const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      JsBarcode(svg, value, {
        format,
        width: 2,
        height: 60,
        displayValue: false,
        margin: 0,
        background: 'transparent',
        lineColor: '#000000',
      });
      svg.setAttribute('width', '100%');
      svg.setAttribute('height', '100%');
      svg.setAttribute('preserveAspectRatio', 'none');
      return svg.outerHTML;
    } catch {
      return `<div class="bc-err">Invalid ${format}: ${esc(value)}</div>`;
    }
  }
  return `<svg class="jsb" data-value="${esc(value)}" data-format="${format}" width="100%" height="100%" preserveAspectRatio="none"></svg>`;
}

/** Inner HTML of one label: text block + price + code block. Template arranges them via CSS. */
export function buildLabelInner(d: LabelData, f: LabelFieldFlags, format: LabelBarcodeFormat): string {
  const text: string[] = [];
  if (f.companyName && has(d.companyName)) text.push(`<div class="l-f l-company">${esc(d.companyName)}</div>`);
  if (f.productName && has(d.productName)) text.push(`<div class="l-name">${esc(d.productName)}</div>`);
  if (f.brand && has(d.brand)) text.push(`<div class="l-f l-brand">${esc(d.brand)}</div>`);
  if (f.category && has(d.category)) text.push(`<div class="l-f l-category">${esc(d.category)}</div>`);
  if (f.hsnCode && has(d.hsnCode)) text.push(`<div class="l-f l-hsn">HSN: ${esc(d.hsnCode)}</div>`);
  if (f.articleNo && has(d.articleNo)) text.push(`<div class="l-f l-article">Art: ${esc(d.articleNo)}</div>`);
  if (f.sizeColor && has(d.sizeColor)) text.push(`<div class="l-f l-sizecolor">${esc(d.sizeColor)}</div>`);
  if (f.customText && has(d.customText)) text.push(`<div class="l-f l-custom">${esc(d.customText)}</div>`);

  const price: string[] = [];
  if (f.mrp && has(d.mrp)) price.push(`<span class="l-mrp">MRP: &#8377;${esc(d.mrp)}</span>`);
  if (f.saleRate && has(d.saleRate)) price.push(`<span class="l-sale">&#8377;${esc(d.saleRate)}</span>`);

  const code: string[] = [];
  if (f.barcodeImage && has(d.barcode)) code.push(`<div class="l-bc">${makeBarcodeSvg(d.barcode, format)}</div>`);
  if (f.barcodeNumber && has(d.barcode)) code.push(`<div class="l-bcno">${esc(d.barcode)}</div>`);

  return (
    `<div class="l-text">${text.join('')}</div>` +
    (price.length ? `<div class="l-price">${price.join('')}</div>` : '') +
    (code.length ? `<div class="l-code">${code.join('')}</div>` : '')
  );
}

/** Label + row CSS. scope = CSS selector prefix (used by preview) */
export function labelCss(
  w: number,
  h: number,
  opts: { scope?: string; layout?: Partial<LabelLayout> | null } = {},
): string {
  const p = opts.scope ? `${opts.scope} ` : '';
  const L = normalizeLayout(opts.layout);
  const S = L.sizes;
  const pad = clamp(Math.min(w, h) * 0.05, 1, 3); // mm
  const center = L.align === 'center';
  const rowW = rollWidth(w, L);
  const side = L.template === 'text-left' || L.template === 'barcode-left';

  const bcH = barcodeHeightMm(w, h, L).toFixed(2);

  const bw = L.barcodeWidth;
  const tw = +(1 - bw).toFixed(2);

  let templateCss = '';
  switch (L.template) {
    case 'barcode-top':
      templateCss = `
${p}.lbl{display:flex;flex-direction:column}
${p}.l-code{order:1;flex:0 0 auto;margin-bottom:0.5mm}
${p}.l-text{order:2;flex:1 1 auto}
${p}.l-price{order:3;flex:0 0 auto}`;
      break;
    case 'text-left':
      templateCss = `
${p}.lbl{display:grid;grid-template-columns:minmax(0,${tw}fr) minmax(0,${bw}fr);grid-template-rows:minmax(0,1fr) auto;grid-template-areas:"text code" "price code";column-gap:1.5mm}
${p}.l-text{grid-area:text}
${p}.l-price{grid-area:price}
${p}.l-code{grid-area:code}`;
      break;
    case 'barcode-left':
      templateCss = `
${p}.lbl{display:grid;grid-template-columns:minmax(0,${bw}fr) minmax(0,${tw}fr);grid-template-rows:minmax(0,1fr) auto;grid-template-areas:"code text" "code price";column-gap:1.5mm}
${p}.l-text{grid-area:text}
${p}.l-price{grid-area:price}
${p}.l-code{grid-area:code}`;
      break;
    default:
      templateCss = `
${p}.lbl{display:flex;flex-direction:column}
${p}.l-text{order:1;flex:1 1 auto}
${p}.l-price{order:2;flex:0 0 auto}
${p}.l-code{order:3;flex:0 0 auto;margin-top:0.5mm}`;
  }

  return `
${p}.l-row{box-sizing:border-box;display:flex;flex-direction:row;gap:${L.colGap}mm;padding:0 ${L.sideMargin}mm;width:${rowW}mm;height:${h}mm;background:#fff}
${p}.lbl{box-sizing:border-box;flex:0 0 ${w}mm;width:${w}mm;height:${h}mm;padding:${pad.toFixed(2)}mm;overflow:hidden;background:#fff;color:#000;font-family:Arial,Helvetica,sans-serif;text-align:${center ? 'center' : 'left'};${L.border ? 'border:0.2mm solid #000;' : ''}}
${p}.lbl *{box-sizing:border-box;margin:0;padding:0}
${p}.l-empty{visibility:hidden}
${p}.l-text{min-height:0;min-width:0;overflow:hidden}
${p}.l-code{display:flex;flex-direction:column;justify-content:center;min-height:0;min-width:0;overflow:hidden}
${p}.l-f{line-height:1.15;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
${p}.l-company{font-size:${S.companyName}px;font-weight:700}
${p}.l-name{font-size:${S.productName}px;font-weight:${L.boldName ? 700 : 400};line-height:1.15;overflow:hidden;display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical}
${p}.l-brand{font-size:${S.brand}px}
${p}.l-category{font-size:${S.category}px}
${p}.l-hsn{font-size:${S.hsnCode}px}
${p}.l-article{font-size:${S.articleNo}px}
${p}.l-sizecolor{font-size:${S.sizeColor}px}
${p}.l-custom{font-size:${S.customText}px}
${p}.l-price{font-weight:700;line-height:1.2;white-space:nowrap;overflow:hidden}
${p}.l-mrp{font-size:${S.mrp}px}
${p}.l-sale{font-size:${S.saleRate}px;margin-left:2mm}
${p}.l-bc{flex:0 0 auto;height:${bcH}mm;padding:0 0.5mm}
${p}.l-bc svg{display:block;width:100%;height:100%}
${p}.l-bcno{font-size:${S.barcodeNumber}px;text-align:center;letter-spacing:0.3px;line-height:1.2;white-space:nowrap;overflow:hidden}
${p}.bc-err{font-size:8px;color:#000}
${templateCss}
`;
}

/** Print labels. Each page = one row of `columns` labels (roll width × label height). */
export function printLabelSheet(
  labels: string[],
  wIn: number,
  hIn: number,
  layoutIn?: Partial<LabelLayout> | null,
): void {
  if (!labels.length) return;
  const { w, h } = clampLabelSize(wIn, hIn);
  const L = normalizeLayout(layoutIn);
  const pageW = rollWidth(w, L);
  const needsCdn = labels.some((l) => l.includes('class="jsb"'));

  const rows: string[] = [];
  for (let i = 0; i < labels.length; i += L.columns) {
    const cells = labels.slice(i, i + L.columns).map((l) => `<div class="lbl">${l}</div>`);
    while (cells.length < L.columns) cells.push('<div class="lbl l-empty"></div>');
    rows.push(`<div class="l-row">${cells.join('')}</div>`);
  }

  const html = `<!DOCTYPE html>
<html>
<head>
<meta charset="UTF-8" />
<title>&nbsp;</title>
${needsCdn ? `<script src="${JSBARCODE_CDN}"><\/script>` : ''}
<style>
@page{size:${pageW}mm ${h}mm;margin:0}
html,body{margin:0 !important;padding:0 !important;width:${pageW}mm;background:#fff;-webkit-print-color-adjust:exact;print-color-adjust:exact}
${labelCss(w, h, { layout: L })}
.l-row{page-break-after:always;break-after:page;page-break-inside:avoid;break-inside:avoid}
.l-row:last-of-type{page-break-after:auto;break-after:auto}
@media screen{body{background:#e5e7eb;width:auto;padding:10px !important}.l-row{margin:0 auto 8px auto;box-shadow:0 0 0 1px #9ca3af}}
</style>
</head>
<body>
${rows.join('')}
<script>
(function(){
  function renderFallback(){
    if (typeof JsBarcode === 'undefined') return;
    var els = document.querySelectorAll('svg.jsb');
    for (var i = 0; i < els.length; i++) {
      var el = els[i];
      try {
        JsBarcode(el, el.getAttribute('data-value'), { format: el.getAttribute('data-format'), width: 2, height: 60, displayValue: false, margin: 0 });
        el.setAttribute('width','100%'); el.setAttribute('height','100%'); el.setAttribute('preserveAspectRatio','none');
      } catch (e) {}
    }
  }
  window.onafterprint = function(){ setTimeout(function(){ window.close(); }, 300); };
  window.onload = function(){
    renderFallback();
    setTimeout(function(){ window.focus(); window.print(); }, 300);
  };
})();
<\/script>
</body>
</html>`;

  const win = window.open('', '_blank');
  if (!win) {
    alert('Popup blocked. Please allow popups for this site to print labels.');
    return;
  }
  win.document.open();
  win.document.write(html);
  win.document.close();
}
